import { useMemo } from "react";
import { Eye, MousePointerClick, Percent, Footprints, Info } from "lucide-react";
import { HeadlineTiles, WhoSawIt, BestAds, WhereAndWhat, rankAds, fmt } from "./StoryParts";
import { audienceGroups, audienceStory } from "./storyData";

const BENCHMARK = 0.07;

const zoneRows = (zones = []) => {
  const list = [...zones].filter((z) => (z.impressions || 0) > 0).sort((a, b) => b.impressions - a.impressions).slice(0, 6);
  const max = Math.max(...list.map((z) => z.impressions), 1);
  const best = Math.max(...list.map((z) => z.ctr || 0), 0);
  return list.map((z) => ({
    label: [z.address, z.city].filter(Boolean).join(", ") || "Geo-fence zone",
    pct: (z.impressions / max) * 100,
    right: `${fmt(z.impressions)} · ${z.ctr || 0}%`,
    highlight: best > 0 && (z.ctr || 0) === best,
  }));
};

export default function MobileStoryLayout({ clientName, mobileSummary, CreativeIcon }) {
  const d = useMemo(() => {
    const o = mobileSummary?.overview || {};
    const strategies = Object.values(mobileSummary?.strategy_breakdown || {}).flatMap((v) => v.strategies || [])
      .map((s) => ({ name: s.strategy_name, impressions: s.impressions, clicks: s.clicks, verified_visits: s.verified_visits }));
    return {
      o,
      actions: (mobileSummary?.conversions || []).reduce((s, c) => s + (c.count || 0), 0),
      aud: audienceGroups(strategies, { splitNear: true }),
      ads: rankAds((mobileSummary?.top_creatives || []).map((c) => ({ name: c.ad_name || c.name, impressions: c.impressions, clicks: c.clicks }))),
      zones: zoneRows(mobileSummary?.geofencing_locations),
      zoneCount: (mobileSummary?.geofencing_locations || []).length,
    };
  }, [mobileSummary]);

  const { o } = d;
  if (!(o.total_impressions > 0)) return null;
  const ctr = parseFloat(o.ctr) || 0;
  const tiles = [
    { icon: Eye, label: "Times seen", value: fmt(o.total_impressions), testId: "mobile-kpi-impressions" },
    { icon: MousePointerClick, label: "Clicks", value: fmt(o.total_clicks), sub: "People who tapped to learn more", testId: "mobile-kpi-clicks" },
    { icon: Percent, label: "Click rate", value: `${ctr.toFixed(2)}%`, sub: "Clicks per ad seen", testId: "mobile-kpi-ctr" },
  ];
  if (o.verified_visits > 0) {
    tiles.push({ icon: Footprints, label: "Verified walk-ins", value: fmt(o.verified_visits), sub: o.estimated_visits ? `About ${fmt(o.estimated_visits)} estimated in total` : null, testId: "mobile-kpi-visits" });
  }
  return (
    <div data-testid="mobile-story-layout">
      <HeadlineTiles tiles={tiles} gauge={{ ctr, benchmark: BENCHMARK, label: "vs typical mobile display ad", testId: "mobile-benchmark-gauge" }} />
      <div className="flex items-start gap-2 bg-white/5 rounded-lg px-3 py-2.5 border border-white/10 -mt-3 mb-6" data-testid="mobile-benchmark-explainer">
        <Info className="w-4 h-4 text-sky-300 flex-shrink-0 mt-0.5" />
        <p className="text-slate-300 text-xs leading-relaxed">
          <span className="text-white font-semibold">About this benchmark:</span> 0.07% is the long-standing industry figure for banner ads on phones and websites, based on Google&apos;s display ad data. It&apos;s the fair comparison because this campaign runs those same placements, not search ads where people are already looking. The verified walk-ins are the strongest proof.
        </p>
      </div>
      {d.aud && <WhoSawIt groups={d.aud.groups} back={d.aud.back} fresh={d.aud.fresh} story={audienceStory(d.aud.back, d.aud.fresh)} testPrefix="mobile" />}
      {d.ads.length > 0 && <BestAds ads={d.ads} CreativeIcon={CreativeIcon} campaignType="mobile_conquesting" benchmark={BENCHMARK} testPrefix="mobile" />}
      <WhereAndWhat testId="mobile-where-what" donut sections={[
        { title: "Busiest geo-fence zones", rows: d.zones, note: d.zoneCount > d.zones.length ? `Top ${d.zones.length} of ${d.zoneCount} zones · views · click rate` : "Views · click rate", testId: "mobile-zones" },
      ]} />
    </div>
  );
}
